<template>
  <van-row class="page-wraper add-card-page">
    <payment-loading-animation
      v-if="showPaymentAnimation"
      :result="paymentAnimationResult"
      @go-home="onAnimationGoHome"
    />
    <van-col v-else span="24" class="flex-display-fix">
      <div class="card-form-container">
        <div class="form-group">
          <label>{{ $t("cardForm.cardNumber") }}</label>
          <custom-input
            v-model="cardData.cardNumber"
            type="text"
            :placeholder="$t('cardForm.cardNumberPlaceholder')"
            :error="errors.cardNumber"
            maxlength="19"
            @input="formatCardNumber"
            @blur="validateCardNumber"
          />
        </div>

        <div class="form-row">
          <div class="form-group half">
            <label>{{ $t("cardForm.expiryDate") }}</label>
            <custom-input
              v-model="cardData.expiry"
              type="text"
              :placeholder="$t('cardForm.expiryDatePlaceholder')"
              :error="errors.expiry"
              maxlength="5"
              @input="formatExpiry"
              @blur="validateExpiry"
            />
          </div>
          <div class="form-group half">
            <label>{{ $t("cardForm.cvv") }}</label>
            <custom-input
              v-model="cardData.cvv"
              type="text"
              :placeholder="$t('cardForm.cvvPlaceholder')"
              :error="errors.cvv"
              maxlength="3"
              @input="formatCvv"
              @blur="validateCvv"
            />
          </div>
        </div>

        <div v-if="cardError" class="card-error">{{ cardError }}</div>

        <button
          class="add-card-btn"
          :disabled="loading || !isFormValid"
          @click="handleSubmit"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
          </svg>
          {{ loading ? $t('cardForm.saving') : $t('cardForm.addCard') }}
        </button>

        <div v-if="success" class="success-message">
          {{ $t("cardForm.cardAddedSuccess") }}
        </div>
      </div>
    </van-col>
  </van-row>
</template>

<script>
import CustomInput from "@/components/forms/CustomInput";
import PaymentLoadingAnimation from "@/components/payment/PaymentLoadingAnimation";

export default {
  name: "AddCardPage",
  components: {
    CustomInput,
    PaymentLoadingAnimation,
  },
  data() {
    return {
      cardData: {
        cardNumber: "",
        expiry: "",
        cvv: "",
      },
      errors: {
        cardNumber: "",
        expiry: "",
        cvv: "",
      },
      loading: false,
      success: false,
      cardError: "",
      showPaymentAnimation: false,
      paymentAnimationResult: null,
    };
  },
  computed: {
    isFormValid() {
      return (
        this.cardData.cardNumber.replace(/\s/g, "").length >= 16 &&
        this.cardData.expiry.length === 5 &&
        this.cardData.cvv.length >= 3
      );
    },
  },
  methods: {
    formatCardNumber() {
      let value = this.cardData.cardNumber.replace(/\s/g, "");
      value = value.replace(/\D/g, "");
      value = value.substring(0, 16);

      const parts = [];
      for (let i = 0; i < value.length; i += 4) {
        parts.push(value.substring(i, i + 4));
      }

      this.cardData.cardNumber = parts.join(" ");

      if (this.errors.cardNumber) {
        this.errors.cardNumber = "";
      }
    },

    validateCardNumber() {
      const cardNumber = this.cardData.cardNumber.replace(/\s/g, "");

      if (!cardNumber) {
        this.errors.cardNumber = this.$t("cardForm.errors.cardNumberRequired");
        return false;
      }

      if (cardNumber.length < 16) {
        this.errors.cardNumber = this.$t("cardForm.errors.cardNumberInvalid");
        return false;
      }

      if (!this.luhnCheck(cardNumber)) {
        this.errors.cardNumber = this.$t("cardForm.errors.cardNumberInvalid");
        return false;
      }

      this.errors.cardNumber = "";
      return true;
    },

    luhnCheck(cardNumber) {
      let sum = 0;
      let isEven = false;

      for (let i = cardNumber.length - 1; i >= 0; i--) {
        let digit = parseInt(cardNumber.charAt(i), 10);

        if (isEven) {
          digit *= 2;
          if (digit > 9) {
            digit -= 9;
          }
        }

        sum += digit;
        isEven = !isEven;
      }

      return sum % 10 === 0;
    },

    formatExpiry() {
      let value = this.cardData.expiry.replace(/\D/g, "");

      if (value.length >= 2) {
        value = value.substring(0, 2) + "/" + value.substring(2, 4);
      }

      this.cardData.expiry = value.substring(0, 5);

      if (this.errors.expiry) {
        this.errors.expiry = "";
      }
    },

    validateExpiry() {
      const expiry = this.cardData.expiry;

      if (!expiry) {
        this.errors.expiry = this.$t("cardForm.errors.expiryRequired");
        return false;
      }

      if (expiry.length !== 5) {
        this.errors.expiry = this.$t("cardForm.errors.expiryInvalid");
        return false;
      }

      const [month] = expiry.split("/");
      const monthNum = parseInt(month, 10);

      if (monthNum < 1 || monthNum > 12) {
        this.errors.expiry = this.$t("cardForm.errors.expiryInvalid");
        return false;
      }

      this.errors.expiry = "";
      return true;
    },

    formatCvv() {
      let value = this.cardData.cvv.replace(/\D/g, "");
      this.cardData.cvv = value.substring(0, 3);

      if (this.errors.cvv) {
        this.errors.cvv = "";
      }
    },

    validateCvv() {
      const cvv = this.cardData.cvv;

      if (!cvv) {
        this.errors.cvv = this.$t("cardForm.errors.cvvRequired");
        return false;
      }

      if (cvv.length < 3) {
        this.errors.cvv = this.$t("cardForm.errors.cvvInvalid");
        return false;
      }

      this.errors.cvv = "";
      return true;
    },

    async handleSubmit() {
      const isCardValid = this.validateCardNumber();
      const isExpiryValid = this.validateExpiry();
      const isCvvValid = this.validateCvv();

      if (!isCardValid || !isExpiryValid || !isCvvValid) {
        return;
      }

      this.loading = true;
      this.cardError = "";

      try {
        const response = await this.$store.dispatch(
          "Payment/saveCardWithFullData",
          {
            user_id: this.$store.state.Auth.user_id,
            card_number: this.cardData.cardNumber.replace(/\s/g, ""),
            card_expiry: this.cardData.expiry,
            card_cvv: this.cardData.cvv,
          }
        );

        console.log("Ответ от бэкенда:", response);

        if (response && response.success) {
          this.success = true;

          await this.$store.dispatch("Payment/getCardsList");

          if (this.$route.params.account) {
            await this.runPaymentAfterCardSaved();
          } else {
            setTimeout(() => {
              this.$router.back();
            }, 1500);
          }
        } else {
          this.cardError =
            response?.error || this.$t("cardForm.errors.saveFailed");
        }
      } catch (e) {
        console.error("Ошибка:", e);
        this.cardError = e.message || this.$t("cardForm.errors.saveFailed");
      }

      this.loading = false;
    },

    async runPaymentAfterCardSaved() {
      const params = this.$route.params;
      const cards = this.$store.state.Payment.cards;

      if (!cards.length) {
        this.cardError = "Картку збережено, але не вдалося запустити оплату";
        return;
      }

      const newCard = cards[cards.length - 1];
      const cardToken = String(newCard.card_token || newCard.id);

      await this.$store.commit("Payment/SET_USER_LS", params.account);
      await this.$store.commit(
        "Payment/SET_NAME_APP_ID",
        this.$store.state.Auth.nativeData?.id || this.$store.state.Auth.app_id
      );
      await this.$store.commit("Payment/SET_CARD_TOKEN", cardToken);

      if (params.naftogazAmount) {
        await this.$store.commit("Payment/SET_NAFTOGAZ_AMOUNT", params.naftogazAmount);
        await this.$store.commit("Payment/UPDATE_AMOUNT", params.naftogazAmount);
      }
      if (params.distributionAmount) {
        await this.$store.commit("Payment/SET_DISTRIBUTION_AMOUNT", params.distributionAmount);
      }

      this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
      this.$store.commit("Payment/SET_PAYMENT_URL", null);

      this.showPaymentAnimation = true;
      this.paymentAnimationResult = null;

      try {
        await this.$store.dispatch("Payment/getPaymentPage");

        const paymentResponse = this.$store.state.Payment.paymentResponse;
        console.log("Payment response after card save:", paymentResponse);

        if (paymentResponse && (paymentResponse.mode === 1 || paymentResponse.status === "success" || paymentResponse.success === true)) {
          this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
          this.paymentAnimationResult = {
            success: true,
            message: paymentResponse.message || "Платіж успішно оброблено",
          };
        } else if (paymentResponse && (paymentResponse.mode === 0 || paymentResponse.status === "error" || paymentResponse.success === false)) {
          this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
          this.paymentAnimationResult = {
            success: false,
            message: paymentResponse.error || paymentResponse.message || "Платіж відхилено банком",
          };
        } else if (paymentResponse && (paymentResponse.status === "Need3Ds" || paymentResponse.mode === 3)) {
          const redirectUrl =
            this.$store.state.Payment.paymentUrl ||
            paymentResponse.action ||
            paymentResponse.alternativeRedirectUrl;
          if (redirectUrl) {
            const platform = this.$store.state.Auth.nativeData?.platform;
            if (platform === "android" && window.Android && typeof window.Android.url === "function") {
              window.Android.url(redirectUrl);
            } else if (platform === "ios" && window.webkit?.messageHandlers?.url) {
              window.webkit.messageHandlers.url.postMessage(redirectUrl);
            } else {
              window.open(redirectUrl, "_blank");
            }
          }
          this.showPaymentAnimation = false;
        } else {
          this.showPaymentAnimation = false;
        }
      } catch (e) {
        console.error("Ошибка при оплате:", e);
        this.paymentAnimationResult = {
          success: false,
          message: "Помилка при обробці платежу",
        };
      }
    },

    async onAnimationGoHome() {
      this.showPaymentAnimation = false;
      this.paymentAnimationResult = null;
      try {
        await this.$router.push({
          name: "Account",
          params: { account: this.$route.params.account },
        });
      } catch (e) {
        if (e.name !== "NavigationDuplicated") throw e;
      }
    },
  },
};
</script>

<style scoped>
.add-card-page {
  min-height: 100vh;
  background-color: #060b11 !important;
}

.card-form-container {
  max-width: 500px;
  margin: 0 auto;
  width: 100%;
  padding: 20px 16px;
  box-sizing: border-box;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: clamp(13px, 3.5vw, 15px);
  text-align: left;
  color: #fff;
}

.form-row {
  display: flex;
  gap: 12px;
}

.half {
  flex: 1;
  min-width: 0;
}

.card-error {
  color: #fff;
  font-size: clamp(12px, 3vw, 14px);
  text-align: center;
  margin-bottom: 15px;
  padding: 12px;
  background-color: rgba(226, 55, 68, 0.2);
  border: 1px solid #e23744;
  border-radius: 8px;
}

.success-message {
  color: #fff;
  font-size: clamp(12px, 3vw, 14px);
  text-align: center;
  margin-top: 15px;
  padding: 12px;
  background-color: rgba(56, 161, 105, 0.2);
  border: 1px solid #38a169;
  border-radius: 8px;
}

.add-card-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100% !important;
  max-width: none !important;
  height: auto !important;
  padding: 1rem;
  margin-top: 20px;
  background: linear-gradient(135deg, #2653c7, #4477f6) !important;
  border: none !important;
  border-radius: 14px !important;
  color: #ffffff !important;
  font-size: clamp(14px, 3.8vw, 1rem);
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(68, 119, 246, 0.35) !important;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.add-card-btn:active:not(:disabled) {
  transform: scale(0.97);
  box-shadow: 0 3px 10px rgba(68, 119, 246, 0.25) !important;
}

.add-card-btn:disabled {
  pointer-events: none;
}

.add-card-page ::v-deep .custom-input {
  background-color: #060b11 !important;
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff !important;
  border-color: rgba(255, 255, 255, 0.2);
  font-size: clamp(14px, 3.8vw, 1rem);
}

.add-card-page ::v-deep .custom-input::placeholder {
  color: rgba(255, 255, 255, 0.5) !important;
}

.add-card-page ::v-deep .custom-input:hover:not(:disabled) {
  border-color: rgba(255, 255, 255, 0.3);
}

@media (max-width: 360px) {
  .card-form-container {
    padding: 16px 12px;
  }
  .form-row {
    gap: 8px;
  }
  .add-card-btn {
    padding: 14px !important;
  }
}

@media (min-width: 768px) {
  .card-form-container {
    padding: 30px 20px;
  }
}
</style>
